import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import moment from 'moment/moment';
import { USER_PREFERENCE_SETS } from 'common/utils/constants';
import { logger } from 'common/utils/helpers';
import { useGetAvailableCountersQuery } from 'apis/pm';
import { selectUserPreferences } from 'redux/selectors';
import { selectUserPreference, patchUserPreference, createUserPreference } from 'redux/UserPreferences';
import Modal from 'components/shared/Modal/Modal';
import { usePreferenceFormResolver } from '../../hooks';
import DeviceChartConfig from './DeviceChartConfig';
import { getDeviceGaVersion } from 'common/utils/deviceTwinUtils';

const defaultValues = {
  name: '',
  state: {
    query: {
      devices: [],
      counters: [],
      actions: [],
      time: {}
    },
    datasets: [],
    scales: []
  }
};

const DeviceChartConfigModal = memo(function DeviceChartConfigModal({
  meta,
  query,
  onChange,
  onClose,
  width,
  height: initialHeight
}) {
  const { preferenceId } = meta;
  const dispatch = useDispatch();
  const preference = useSelector((state) => selectUserPreference(selectUserPreferences(state), preferenceId));
  const selectedDevice = useSelector((state) => state.ui.singleDevicePage.selectedDevice);
  const resolver = usePreferenceFormResolver({ category: USER_PREFERENCE_SETS.CHART });
  const createPreference = useCallback(
    async (values) => {
      try {
        const result = await dispatch(
          createUserPreference({
            category: USER_PREFERENCE_SETS.CHART,
            default: false,
            name: values.name,
            state: values.state
          })
        );
        onClose();
        onChange({
          preferenceId: result.preferenceId,
          category: result.category,
          query: {
            actions: values.state.query.actions
          }
        });
      } catch (error) {
        logger.error(error);
      }
    },
    [dispatch, onClose, onChange]
  );
  const updatePreference = useCallback(
    async (values) => {
      try {
        await dispatch(
          patchUserPreference({
            preferenceId: values.preferenceId,
            category: values.category,
            name: values.name,
            state: values.state
          })
        );
        onClose();
        onChange({
          preferenceId: values.preferenceId,
          category: values.category,
          query: {
            actions: values.state.query.actions
          }
        });
      } catch (error) {
        logger.error(error);
      }
    },
    [dispatch, onClose, onChange]
  );
  const handleSubmit = useCallback(
    (values) => {
      if (values.local) {
        return;
      }

      return updatePreference(values);
    },
    [updatePreference]
  );
  const initialValues = useMemo(
    () => ({
      ...preference,
      state: {
        ...preference.state,
        query: {
          ...preference.state.query,
          ...query
        }
      }
    }),
    [preference, query]
  );
  const methods = useForm({
    mode: 'onBlur',
    defaultValues,
    values: initialValues,
    resolver
  });
  const [from, to] = useMemo(() => {
    const toDate = new Date(Date.now());
    const fromDate = moment(toDate).subtract(2, 'years').toDate();

    return [fromDate.toISOString(), toDate.toISOString()];
  }, []);
  const availableCountersQuery = useGetAvailableCountersQuery(
    {
      source: 'pm',
      from,
      to
    },
    {
      placeholderData: []
    },
    getDeviceGaVersion(selectedDevice)
  );
  const [height, setHeight] = useState(initialHeight);
  const { control } = methods;
  const datasets = useWatch({ name: 'state.datasets', control });
  const datasetsCount = datasets.length;

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    setHeight(getHeight(initialHeight, Math.max(1, datasetsCount)));
  }, [initialHeight, datasetsCount]);

  return (
    <Modal
      width={width + 80}
      height={height + 198}
      noPadding={true}
      colorize={'#f8f9fa'}
      opaque={true}
      overflow={true}
      global
      close={handleClose}
    >
      <FormProvider {...methods}>
        <DeviceChartConfig
          availableCounters={availableCountersQuery.data}
          onCancel={handleClose}
          onSubmit={handleSubmit}
          width={width}
          height={height}
          createPreference={createPreference}
          local={preference.local}
        />
      </FormProvider>
    </Modal>
  );
});

export default DeviceChartConfigModal;

function getHeight(baseHeight, count) {
  return baseHeight + count * (count !== 3 ? 130 : 120);
}
